import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { CallbackView } from '../components/CallbackView.js';
import { exchangeCodeForTokens } from '../lib/auth.js';

type CallbackSearch = {
  readonly code?: string | undefined;
  readonly state?: string | undefined;
  readonly error?: string | undefined;
};

export const Route = createFileRoute('/callback')({
  component: CallbackRoute,
  validateSearch: (search: Record<string, unknown>): CallbackSearch => ({
    code: typeof search['code'] === 'string' ? search['code'] : undefined,
    state: typeof search['state'] === 'string' ? search['state'] : undefined,
    error: typeof search['error'] === 'string' ? search['error'] : undefined,
  }),
});

function CallbackRoute() {
  const navigate = useNavigate();
  const { code, state, error } = Route.useSearch();
  const [failure, setFailure] = useState<string | undefined>();

  useEffect(() => {
    // Okta returned an error param, or the user cancelled at the IdP.
    if (error !== undefined) {
      setFailure('Sign-in was cancelled or failed at the identity provider.');
      return;
    }
    if (code === undefined || state === undefined) {
      setFailure('Sign-in is missing its authorization code. Please sign in again.');
      return;
    }
    let active = true;
    void exchangeCodeForTokens(code, state)
      .then(() => {
        if (active) void navigate({ to: '/', replace: true });
      })
      .catch((caught: unknown) => {
        if (active) {
          setFailure(
            caught instanceof Error ? caught.message : 'Sign-in failed. Please try again.',
          );
        }
      });
    return () => {
      active = false;
    };
  }, [code, state, error, navigate]);

  return <CallbackView {...(failure === undefined ? {} : { error: failure })} />;
}
